import { useAuth } from '@agentnet/auth';
import { FincenReportContext, FincenReportContextInterface } from 'hooks/FincenReportContext';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import useSnackBars from 'ui-kit/components/notification/useSnackbars';

interface UseAutoSaveOptions {
  /** Function to save the current data */
  saveFunction: () => Promise<void> | void;
  /** Auto-save interval in milliseconds (default: 60000ms = 1 minute) */
  interval?: number;
  /** Whether auto-save is enabled */
  enabled?: boolean;
  /** Data dependencies to watch for changes */
  dependencies: unknown[];
  /** Optional callback when auto-save succeeds */
  onSaveSuccess?: () => void;
  /** Optional callback when auto-save fails */
  onSaveError?: (error: unknown) => void;
  /** Whether to show notifications for auto-save */
  showNotifications?: boolean;
}

interface UseAutoSaveReturn {
  /** Manually trigger a save */
  triggerSave: () => void;
  /** Whether auto-save is currently running */
  isSaving: boolean;
  /** Reset the auto-save timer */
  resetTimer: () => void;
}

/**
 * Hook to provide auto-save functionality for FinCEN pages
 * Automatically saves data every 1 minute when changes are detected
 */
export const useAutoSave = ({
  saveFunction,
  interval = 60000, // 1 minute
  enabled = true,
  dependencies,
  onSaveSuccess,
  onSaveError,
  showNotifications = false,
}: UseAutoSaveOptions): UseAutoSaveReturn => {
  const { addSnackbarMessage } = useSnackBars();
  const [timer, setTimer] = useState<NodeJS.Timeout | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSavedData, setLastSavedData] = useState<string>('');

  const triggerSave = useCallback(async () => {
    if (isSaving || !enabled) {
      return;
    }

    try {
      setIsSaving(true);
      await saveFunction();

      if (showNotifications) {
        addSnackbarMessage({
          message: 'Auto-saved successfully',
          type: 'success',
          autoHideDuration: 2000,
        });
      }

      onSaveSuccess?.();
    } catch (error) {
      console.error('Auto-save failed:', error);

      if (showNotifications) {
        addSnackbarMessage({
          message: 'Auto-save failed',
          type: 'error',
          autoHideDuration: 3000,
        });
      }

      onSaveError?.(error);
    } finally {
      setIsSaving(false);
    }
  }, [saveFunction, enabled, showNotifications, addSnackbarMessage, onSaveSuccess, onSaveError, isSaving]);

  const resetTimer = useCallback(() => {
    if (timer) {
      clearTimeout(timer);
      setTimer(null);
    }
  }, [timer]);

  // Simple effect that only depends on dependencies array
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const currentDataString = JSON.stringify(dependencies);

    // Check if this is different from last saved
    if (currentDataString === lastSavedData) {
      return;
    }

    // Update the last saved data
    setLastSavedData(currentDataString);

    // Clear any existing timer
    if (timer) {
      clearTimeout(timer);
    }

    // Set new timer
    const newTimer = setTimeout(() => {
      triggerSave();
    }, interval);

    setTimer(newTimer);

    return () => {
      clearTimeout(newTimer);
    };
  }, [JSON.stringify(dependencies), enabled, interval]);

  // Cleanup effect
  useEffect(() => {
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, []);

  return {
    triggerSave,
    isSaving,
    resetTimer,
  };
};

/**
 * Configuration for FinCEN auto-save functionality
 */
export interface FincenAutoSaveConfig<T> {
  /** The data to be saved */
  data: T | undefined;
  /** Function to update local state */
  updateFunction?: (data: T) => void;
  /** Function to save data via API call */
  apiSaveFunction: (reportId: string, data: any, token: string) => Promise<any>;
  /** The FinCEN report ID */
  reportId: string | number | undefined;
  /** Function to check if data has meaningful content that should be saved */
  hasContent: (data: T) => boolean;
  /** Optional function to transform data before API call */
  transformDataForAPI?: (data: T) => any;
  /** Whether auto-save is enabled */
  enabled?: boolean;
  /** Whether to show notifications */
  showNotifications?: boolean;
  /** Error message context for logging */
  errorContext?: string;
}

/**
 * Generic hook for FinCEN auto-save functionality
 * Consolidates the common pattern used across FinCEN reporting components
 */
export const useFincenAutoSave = <T>({
  data,
  updateFunction,
  apiSaveFunction,
  reportId,
  hasContent,
  transformDataForAPI,
  enabled = true,
  showNotifications = true,
  errorContext = 'FinCEN data',
}: FincenAutoSaveConfig<T>) => {
  const { getAccessToken } = useAuth();

  const performAutoSave = useCallback(async () => {
    if (data && updateFunction && hasContent(data)) {
      updateFunction(data);
      const token = await getAccessToken();
      const dataToSave = transformDataForAPI ? transformDataForAPI(data) : data;
      await apiSaveFunction(String(reportId || 0), dataToSave, token);
    } else if (data && !updateFunction && hasContent(data)) {
      // For cases where there's no local state update function (like filing information)
      const token = await getAccessToken();
      const dataToSave = transformDataForAPI ? transformDataForAPI(data) : data;
      await apiSaveFunction(String(reportId || 0), dataToSave, token);
    }
  }, [updateFunction, data, hasContent, getAccessToken, transformDataForAPI, apiSaveFunction, reportId]);

  return useAutoSave({
    saveFunction: performAutoSave,
    dependencies: [data],
    enabled,
    showNotifications,
    onSaveError: (error: any) => {
      console.error(`Auto-save failed for ${errorContext}:`, error);
    },
  });
};

export const useAutoSaveNavigation = (
  sectionName: string,
  saveHandler: () => Promise<void>,
  fileId: string | undefined,
  enabled = true,
) => {
  const fincenReportCtx = useContext<FincenReportContextInterface | null>(FincenReportContext);
  const history = useHistory();
  const [isNavigating, setIsNavigating] = useState(false);

  const stableSaveHandler = useCallback(async () => {
    await saveHandler();
  }, [saveHandler]);

  useEffect(() => {
    if (!enabled || !fincenReportCtx?.registerSaveHandler) return;

    fincenReportCtx.registerSaveHandler(sectionName, stableSaveHandler);

    return () => {
      if (fincenReportCtx?.unregisterSaveHandler) {
        fincenReportCtx.unregisterSaveHandler(sectionName);
      }
    };
  }, [sectionName, enabled, fincenReportCtx, stableSaveHandler]);

  useEffect(() => {
    const pendingPath = fincenReportCtx?.pendingNavigation;

    if (!pendingPath || isNavigating || !fincenReportCtx) return;

    const executeNavigation = async () => {
      setIsNavigating(true);
      try {
        await stableSaveHandler();

        // Reload fincen report from server after saving to get fresh data
        if (fileId && fincenReportCtx.reloadFincenReport) {
          await fincenReportCtx.reloadFincenReport(fileId);
        }

        if (fincenReportCtx.setPendingNavigation) {
          fincenReportCtx.setPendingNavigation(null);
        }
        history.push(pendingPath);
      } catch (error) {
        if (fincenReportCtx.setPendingNavigation) {
          fincenReportCtx.setPendingNavigation(null);
        }
      } finally {
        setIsNavigating(false);
      }
    };

    executeNavigation();
  }, [
    fincenReportCtx?.pendingNavigation,
    fileId,
    history,
    fincenReportCtx,
    sectionName,
    stableSaveHandler,
    isNavigating,
  ]);
};

export default useAutoSave;
